import {z} from 'zod';
import {parse, isAfter} from 'date-fns';
import {SchemaValidationErrorLabel} from '@netx/core-schema';

export enum UploadAssetRequestNotifyType {
	NONE,
	USER,
	GROUP,
}

export enum UploadAssetRequestStatus {
	ACTIVE = 100,
	USER_DISABLED = 200,
	EXPIRED = 10034,
	CATEGORY_RESOURCE_MISSING = 10036,
	USER_RESOURCE_MISSING = 10037,
}

export const UploadAssetRequestAttributeScheme = z.object({
	id: z.number().int().gt(0).optional(),
	templateId: z.number().int().gt(0),
	label: z.string(),
	placeholder: z.string(),
	caption: z.string(),
	required: z.boolean(),
	values: z.array(z.string()),
});

export const UploadAssetRequestNotifySchema = z.object({
	notifyId: z.number().int().gt(0, 'Invalid `notifyId`').nullable(),
	notifyLabel: z.string().nullable(),
	notifyType: z.nativeEnum(UploadAssetRequestNotifyType),
});

/** validate against the record that gets sent to the server */
export const UploadAssetRequestSchema = z
	.object({
		id: z.number().int().gt(0, 'Invalid `id`'),
		attributeEditsEnabled: z.boolean(),
		attributes: z.array(UploadAssetRequestAttributeScheme),
		categoryId: z.number().int().gt(1, SchemaValidationErrorLabel.REQUIRED),
		categoryName: z.string(),
		categoryPath: z.string(),
		creationDate: z.string(),
		creatorUserId: z.number().int().gt(0, 'Invalid `creatorUserId`').nullable(),
		creatorUserLabel: z.string().nullable(),
		status: z.nativeEnum(UploadAssetRequestStatus),
		expirationDate: z
			.string({description: 'yyyy-mm-dd'})
			.regex(/^\d\d\d\d-\d\d-\d\d$/, SchemaValidationErrorLabel.DATE_INVALID)
			.optional()
			.nullable(),
		title: z.string().nonempty(SchemaValidationErrorLabel.REQUIRED),
		token: z.string(),
		url: z.string(),
	})
	.extend(UploadAssetRequestNotifySchema.shape);

export default UploadAssetRequestSchema;

/** validate against a new upload asset request */
export const UploadAssetRequestSchemaNew = UploadAssetRequestSchema.omit({
	id: true,
	creationDate: true,
	creatorUserId: true,
	creatorUserLabel: true,
	token: true,
	url: true,
}).partial({
	categoryName: true,
	categoryPath: true,
	expirationDate: true,
	notifyId: true,
	notifyLabel: true,
});

/** all upload asset request fields */
export const UploadAssetRequestField = UploadAssetRequestSchema.keyof().enum;

/** a true upload asset request record */
export type IUploadAssetRequest = z.infer<typeof UploadAssetRequestSchema>;

/** a new upload asset request record */
export type IUploadAssetRequestNew = z.infer<
	typeof UploadAssetRequestSchemaNew
>;

export type IUploadAssetRequestAttribute = z.infer<
	typeof UploadAssetRequestAttributeScheme
>;

/** refinement for expiration date */
export const UploadAssetRequestExpirationDateSchemaCheck = (
	{id, expirationDate, status}: Partial<IUploadAssetRequest>,
	ctx: z.RefinementCtx,
) => {
	if (
		status !== UploadAssetRequestStatus.EXPIRED &&
		expirationDate &&
		!isAfter(parse(expirationDate, 'yyyy-MM-dd', new Date()), new Date())
	) {
		ctx.addIssue({
			fatal: true,
			code: z.ZodIssueCode.invalid_date,
			path: ['expirationDate'],
			message: SchemaValidationErrorLabel.DATE_MUST_BE_IN_THE_FUTURE,
		});
	}
};

/** refinement for notify */
export const UploadAssetRequestNotifySchemaCheck = (
	{notifyType, notifyId}: Partial<IUploadAssetRequest>,
	ctx: z.RefinementCtx,
) => {
	// `notifyId` is set but `notifyType` is not
	if (notifyType === UploadAssetRequestNotifyType.NONE && notifyId) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['notifyId'],
			message: '`notifyType` must be set when `notifyId` is not `0`',
		});
	}
	// `notifyType` is set but `notifyId` is not
	if (!notifyId && notifyType !== UploadAssetRequestNotifyType.NONE) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['notifyType'],
			message: '`notifyId` must be set when `notifyType` is not "none"',
		});
	}
};

/** helper function for wrapping the schema in all refinements */
export const applyUploadAssetRequestSchemaEffects = <T extends z.ZodRawShape>(
	schema: z.ZodObject<T>,
) => {
	return schema
		.superRefine(UploadAssetRequestNotifySchemaCheck)
		.superRefine(UploadAssetRequestExpirationDateSchemaCheck);
};

/**
 * default refined schema\
 * note: using this directly will not hit any refinements should any of the initial schema validations fail\
 * if you want the refinements to be hit, regardless of the initial schema validation failing then call the
 * `applyUploadAssetRequestSchemaEffects` function directly and supply a `partial` schema
 * @example
 * ```ts
 * applyUploadAssetRequestSchemaEffects(UploadAssetRequestSchemaRefined.partial())
 * ```
 */
export const UploadAssetRequestSchemaRefined =
	applyUploadAssetRequestSchemaEffects(UploadAssetRequestSchema);
